export interface AdminViolationStatistics {
  level: "РК" | "Область" | "Регион";
  region: string;
  authority: string; // Орган принявший решение
  prosecutorOffice: string; // Контрольный орган ведомства

  // 1. Регистрация
  registration: {
    totalRegistered: number; // Всего зарегистрировано
    notReviewed: number; // Не рассмотрено
  };

  // 2. Рассмотрение
  review: {
    total: number; // Рассмотрено всего
    byCourt: number; // Рассмотрено судом
    byAuthority: number; // Рассмотрено уполномоченным органом
    efficiency: number; // % рассмотрения
  };

  // 3. КУИ
  kui: {
    total: number;
    withProtocol: number; // С номером протокола
    withoutProtocol: number; // Без номера протокола
    quality: number; // % с протоколом
  };

  // 4. Постановления
  decisions: {
    terminated: number; // О прекращении (в делах)
    penalty: {
      total: number; // О наложении взыскания (в делах)
      individuals: number; // Привлечено физических лиц
      legalEntities: number; // Привлечено юридических лиц
    };
  };

  // 5. Основные взыскания
  penaltiesMain: {
    warning: number;
    fine: number;
    arrest: number;
    licenseDeprivation: number;
    deportation: number;
    licenseSuspension: number;
  };

  // 6. Дополнительные взыскания
  penaltiesAdditional: {
    licenseDeprivation: number;
    deportation: number;
    licenseSuspension: number;
    activitySuspension: number;
    confiscation: number;
  };

  // 7. Исполнение штрафов
  fineExecution: {
    damageAmount: number; // Ущерб государству
    collected: number; // Взыскано в пользу государства
    fineAmount: {
      imposed: number; // Наложенного
      collected: number; // Взысканного
    };
    simplifiedProcedure: number; // По сокращенному производству
    article819p2: number; // По ч.2 ст.819
    forcedExecution: {
      sent: number; // Направлено на принудительное
      executed: {
        persons: number; // Исполнено (в лицах)
        amount: number; // На сумму
      };
    };
    decisionsCount: number; // Постановлений по взысканному штрафу
    article897p1: {
      count: number; // По ч.1 ст.897
      amount: number;
    };
    courtFines: {
      imposed: number; // Наложенные судами
      collected: number; // Взысканные судами
    };
    efficiency: number; // % взыскания
  };

  // 8. Специальные категории
  specialCategories: {
    individualEntrepreneurs: number; // ИП
    minors: number; // Несовершеннолетние
    officials: number; // Должностные лица (ст.30)
    foreignCitizens: number; // Иностранные граждане
  };

  // 9. Законность
  legality: {
    terminatedByProsecutor: number; // Прекращено ведомством
    cancelledByCourt: number; // Отменено судом
    proceduralViolations: number;
    avgProcessingTime: number; // Средние сроки (дней)
    quality: number; // % законных решений
  };
}

// Общие KPI по РК
export const rkOverview = {
  totalRegistered: 395000,
  notReviewed: 28500,
  reviewEfficiency: 92.8, // % рассмотрения
  kuiTotal: 12450,
  kuiQuality: 87.5, // % с протоколом
  fineCollected: 12500000000, // 12.5 млрд тенге
  fineEfficiency: 78.5, // % взыскания
  legalityQuality: 98.9, // % законных решений
  dynamics: +1.8, // Динамика к прошлому периоду
};

// Данные по регионам (агрегированные)
export const regionsData: AdminViolationStatistics[] = [
  {
    level: "Регион",
    region: "г. Атырау",
    authority: "Все органы",
    prosecutorOffice: "Акимат г. Атырау",
    registration: {
      totalRegistered: 185000,
      notReviewed: 12500,
    },
    review: {
      total: 172500,
      byCourt: 125000,
      byAuthority: 47500,
      efficiency: 93.2,
    },
    kui: {
      total: 5800,
      withProtocol: 5100,
      withoutProtocol: 700,
      quality: 87.9,
    },
    decisions: {
      terminated: 15200,
      penalty: {
        total: 157300,
        individuals: 142000,
        legalEntities: 15300,
      },
    },
    penaltiesMain: {
      warning: 12500,
      fine: 138000,
      arrest: 1200,
      licenseDeprivation: 3200,
      deportation: 1800,
      licenseSuspension: 600,
    },
    penaltiesAdditional: {
      licenseDeprivation: 800,
      deportation: 450,
      licenseSuspension: 320,
      activitySuspension: 150,
      confiscation: 280,
    },
    fineExecution: {
      damageAmount: 4500000000,
      collected: 3650000000,
      fineAmount: {
        imposed: 4650000000,
        collected: 3650000000,
      },
      simplifiedProcedure: 850000000,
      article819p2: 1250,
      forcedExecution: {
        sent: 8500,
        executed: {
          persons: 6800,
          amount: 980000000,
        },
      },
      decisionsCount: 142000,
      article897p1: {
        count: 3200,
        amount: 850000000,
      },
      courtFines: {
        imposed: 1250000000,
        collected: 980000000,
      },
      efficiency: 78.5,
    },
    specialCategories: {
      individualEntrepreneurs: 12500,
      minors: 3200,
      officials: 8500,
      foreignCitizens: 18500,
    },
    legality: {
      terminatedByProsecutor: 15200,
      cancelledByCourt: 2035,
      proceduralViolations: 850,
      avgProcessingTime: 12.5,
      quality: 98.9,
    },
  },
  {
    level: "Регион",
    region: "Жылыойский район",
    authority: "Все органы",
    prosecutorOffice: "Акимат Жылыойский район",
    registration: {
      totalRegistered: 152000,
      notReviewed: 9800,
    },
    review: {
      total: 142200,
      byCourt: 102000,
      byAuthority: 40200,
      efficiency: 93.6,
    },
    kui: {
      total: 4800,
      withProtocol: 4250,
      withoutProtocol: 550,
      quality: 88.5,
    },
    decisions: {
      terminated: 12500,
      penalty: {
        total: 129700,
        individuals: 118000,
        legalEntities: 11700,
      },
    },
    penaltiesMain: {
      warning: 10200,
      fine: 112000,
      arrest: 980,
      licenseDeprivation: 2800,
      deportation: 1500,
      licenseSuspension: 520,
    },
    penaltiesAdditional: {
      licenseDeprivation: 650,
      deportation: 380,
      licenseSuspension: 280,
      activitySuspension: 120,
      confiscation: 240,
    },
    fineExecution: {
      damageAmount: 3800000000,
      collected: 2980000000,
      fineAmount: {
        imposed: 3800000000,
        collected: 2980000000,
      },
      simplifiedProcedure: 720000000,
      article819p2: 1050,
      forcedExecution: {
        sent: 7200,
        executed: {
          persons: 5800,
          amount: 820000000,
        },
      },
      decisionsCount: 118000,
      article897p1: {
        count: 2800,
        amount: 720000000,
      },
      courtFines: {
        imposed: 1020000000,
        collected: 800000000,
      },
      efficiency: 78.4,
    },
    specialCategories: {
      individualEntrepreneurs: 10200,
      minors: 2800,
      officials: 7200,
      foreignCitizens: 15200,
    },
    legality: {
      terminatedByProsecutor: 12500,
      cancelledByCourt: 1680,
      proceduralViolations: 680,
      avgProcessingTime: 11.8,
      quality: 99.1,
    },
  },
  {
    level: "Регион",
    region: "Курмангазинский район",
    authority: "Все органы",
    prosecutorOffice: "Акимат Курмангазинский район",
    registration: {
      totalRegistered: 68000,
      notReviewed: 4200,
    },
    review: {
      total: 63800,
      byCourt: 45000,
      byAuthority: 18800,
      efficiency: 93.8,
    },
    kui: {
      total: 2100,
      withProtocol: 1850,
      withoutProtocol: 250,
      quality: 88.1,
    },
    decisions: {
      terminated: 5600,
      penalty: {
        total: 58200,
        individuals: 52800,
        legalEntities: 5400,
      },
    },
    penaltiesMain: {
      warning: 4800,
      fine: 50000,
      arrest: 420,
      licenseDeprivation: 1200,
      deportation: 680,
      licenseSuspension: 100,
    },
    penaltiesAdditional: {
      licenseDeprivation: 280,
      deportation: 180,
      licenseSuspension: 120,
      activitySuspension: 60,
      confiscation: 100,
    },
    fineExecution: {
      damageAmount: 1650000000,
      collected: 1280000000,
      fineAmount: {
        imposed: 1630000000,
        collected: 1280000000,
      },
      simplifiedProcedure: 320000000,
      article819p2: 480,
      forcedExecution: {
        sent: 3200,
        executed: {
          persons: 2500,
          amount: 360000000,
        },
      },
      decisionsCount: 52800,
      article897p1: {
        count: 1200,
        amount: 320000000,
      },
      courtFines: {
        imposed: 450000000,
        collected: 350000000,
      },
      efficiency: 78.5,
    },
    specialCategories: {
      individualEntrepreneurs: 4800,
      minors: 1200,
      officials: 3200,
      foreignCitizens: 6800,
    },
    legality: {
      terminatedByProsecutor: 5600,
      cancelledByCourt: 750,
      proceduralViolations: 320,
      avgProcessingTime: 13.2,
      quality: 98.7,
    },
  },
  {
    level: "Регион",
    region: "Махамбетский район",
    authority: "Все органы",
    prosecutorOffice: "Акимат Алматинской области",
    registration: {
      totalRegistered: 52000,
      notReviewed: 3200,
    },
    review: {
      total: 48800,
      byCourt: 35000,
      byAuthority: 13800,
      efficiency: 93.8,
    },
    kui: {
      total: 1650,
      withProtocol: 1450,
      withoutProtocol: 200,
      quality: 87.9,
    },
    decisions: {
      terminated: 4300,
      penalty: {
        total: 44500,
        individuals: 40500,
        legalEntities: 4000,
      },
    },
    penaltiesMain: {
      warning: 3600,
      fine: 38500,
      arrest: 320,
      licenseDeprivation: 920,
      deportation: 520,
      licenseSuspension: 80,
    },
    penaltiesAdditional: {
      licenseDeprivation: 220,
      deportation: 140,
      licenseSuspension: 90,
      activitySuspension: 45,
      confiscation: 75,
    },
    fineExecution: {
      damageAmount: 1280000000,
      collected: 980000000,
      fineAmount: {
        imposed: 1250000000,
        collected: 980000000,
      },
      simplifiedProcedure: 250000000,
      article819p2: 380,
      forcedExecution: {
        sent: 2500,
        executed: {
          persons: 1950,
          amount: 280000000,
        },
      },
      decisionsCount: 40500,
      article897p1: {
        count: 920,
        amount: 250000000,
      },
      courtFines: {
        imposed: 350000000,
        collected: 270000000,
      },
      efficiency: 78.4,
    },
    specialCategories: {
      individualEntrepreneurs: 3600,
      minors: 920,
      officials: 2500,
      foreignCitizens: 5200,
    },
    legality: {
      terminatedByProsecutor: 4300,
      cancelledByCourt: 580,
      proceduralViolations: 250,
      avgProcessingTime: 12.8,
      quality: 98.8,
    },
  },
  {
    level: "Регион",
    region: "Исатайский район",
    authority: "Все органы",
    prosecutorOffice: "Акимат Карагандинской области",
    registration: {
      totalRegistered: 48000,
      notReviewed: 2900,
    },
    review: {
      total: 45100,
      byCourt: 32000,
      byAuthority: 13100,
      efficiency: 94.0,
    },
    kui: {
      total: 1520,
      withProtocol: 1340,
      withoutProtocol: 180,
      quality: 88.2,
    },
    decisions: {
      terminated: 3950,
      penalty: {
        total: 41150,
        individuals: 37500,
        legalEntities: 3650,
      },
    },
    penaltiesMain: {
      warning: 3300,
      fine: 35500,
      arrest: 290,
      licenseDeprivation: 850,
      deportation: 480,
      licenseSuspension: 70,
    },
    penaltiesAdditional: {
      licenseDeprivation: 200,
      deportation: 130,
      licenseSuspension: 85,
      activitySuspension: 40,
      confiscation: 70,
    },
    fineExecution: {
      damageAmount: 1180000000,
      collected: 920000000,
      fineAmount: {
        imposed: 1150000000,
        collected: 920000000,
      },
      simplifiedProcedure: 230000000,
      article819p2: 350,
      forcedExecution: {
        sent: 2300,
        executed: {
          persons: 1800,
          amount: 260000000,
        },
      },
      decisionsCount: 37500,
      article897p1: {
        count: 850,
        amount: 230000000,
      },
      courtFines: {
        imposed: 320000000,
        collected: 250000000,
      },
      efficiency: 80.0,
    },
    specialCategories: {
      individualEntrepreneurs: 3300,
      minors: 850,
      officials: 2300,
      foreignCitizens: 4800,
    },
    legality: {
      terminatedByProsecutor: 3950,
      cancelledByCourt: 530,
      proceduralViolations: 230,
      avgProcessingTime: 12.5,
      quality: 98.9,
    },
  },
];

// Данные по органам (для детализации)
export const authoritiesData = [
  {
    authority: "Полиция",
    totalRegistered: 185000,
    reviewEfficiency: 92.5,
    kuiTotal: 5800,
    terminated: 15200,
    cancelled: 2035,
    fineCollected: 3650000000,
  },
  {
    authority: "Миграционная служба",
    totalRegistered: 42000,
    reviewEfficiency: 94.2,
    kuiTotal: 1250,
    terminated: 3450,
    cancelled: 462,
    fineCollected: 850000000,
  },
  {
    authority: "Налоговые органы",
    totalRegistered: 98000,
    reviewEfficiency: 93.8,
    kuiTotal: 2900,
    terminated: 8040,
    cancelled: 1078,
    fineCollected: 2100000000,
  },
  {
    authority: "КУИС",
    totalRegistered: 35000,
    reviewEfficiency: 92.0,
    kuiTotal: 1050,
    terminated: 2870,
    cancelled: 385,
    fineCollected: 750000000,
  },
  {
    authority: "Транспортная инспекция",
    totalRegistered: 25000,
    reviewEfficiency: 95.0,
    kuiTotal: 750,
    terminated: 2050,
    cancelled: 275,
    fineCollected: 520000000,
  },
];

// Динамика по месяцам
export const monthlyDynamics = [
  {
    month: "Янв",
    registered: 32500,
    reviewed: 30200,
    notReviewed: 2300,
    kui: 1050,
    fineCollected: 980000000,
  },
  {
    month: "Фев",
    registered: 29800,
    reviewed: 27800,
    notReviewed: 2000,
    kui: 950,
    fineCollected: 920000000,
  },
  {
    month: "Мар",
    registered: 34200,
    reviewed: 31800,
    notReviewed: 2400,
    kui: 1100,
    fineCollected: 1050000000,
  },
  {
    month: "Апр",
    registered: 36800,
    reviewed: 34200,
    notReviewed: 2600,
    kui: 1200,
    fineCollected: 1120000000,
  },
  {
    month: "Май",
    registered: 41200,
    reviewed: 38200,
    notReviewed: 3000,
    kui: 1350,
    fineCollected: 1250000000,
  },
  {
    month: "Июн",
    registered: 45600,
    reviewed: 42200,
    notReviewed: 3400,
    kui: 1500,
    fineCollected: 1380000000,
  },
  {
    month: "Июл",
    registered: 48200,
    reviewed: 44800,
    notReviewed: 3400,
    kui: 1600,
    fineCollected: 1450000000,
  },
  {
    month: "Авг",
    registered: 43800,
    reviewed: 40800,
    notReviewed: 3000,
    kui: 1450,
    fineCollected: 1320000000,
  },
  {
    month: "Сен",
    registered: 39500,
    reviewed: 36800,
    notReviewed: 2700,
    kui: 1300,
    fineCollected: 1180000000,
  },
  {
    month: "Окт",
    registered: 36200,
    reviewed: 33800,
    notReviewed: 2400,
    kui: 1150,
    fineCollected: 1080000000,
  },
  {
    month: "Ноя",
    registered: 32800,
    reviewed: 30500,
    notReviewed: 2300,
    kui: 1050,
    fineCollected: 980000000,
  },
  {
    month: "Дек",
    registered: 35400,
    reviewed: 33000,
    notReviewed: 2400,
    kui: 1120,
    fineCollected: 1050000000,
  },
];

// Топ-5 проблемных регионов
export const topProblemRegions = [
  {
    region: "г. Атырау",
    violations: 185000,
    per100k: 1210,
    trend: +2.5,
    kui: 5800,
    legality: 98.9,
  },
  {
    region: "Жылыойский район",
    violations: 152000,
    per100k: 1165,
    trend: +1.8,
    kui: 4800,
    legality: 99.1,
  },
  {
    region: "Курмангазинский район",
    violations: 68000,
    per100k: 1250,
    trend: +3.1,
    kui: 2100,
    legality: 98.7,
  },
  {
    region: "Махамбетский район",
    violations: 52000,
    per100k: 940,
    trend: +0.9,
    kui: 1650,
    legality: 98.8,
  },
  {
    region: "Исатайский район",
    violations: 48000,
    per100k: 855,
    trend: -0.5,
    kui: 1520,
    legality: 98.9,
  },
];

// Данные для воронки жизненного цикла
export const lifecycleFunnel = [
  { stage: "Зарегистрировано", value: 395000 },
  { stage: "Рассмотрено", value: 366500 },
  { stage: "Постановления", value: 351200 },
  { stage: "Взыскания", value: 342000 },
  { stage: "Исполнено", value: 268500 },
];

// Данные для карты (регионы с интенсивностью)
export const mapRegionsData = regionsData.map((r) => ({
  name: r.region,
  violations: r.registration.totalRegistered,
  level:
    r.registration.totalRegistered > 150000
      ? "critical"
      : r.registration.totalRegistered > 100000
      ? "high"
      : r.registration.totalRegistered > 50000
      ? "medium"
      : "low",
  lat: 0, // Будет заполнено в компоненте
  lng: 0,
}));

// -----------------------------------------------------------------------------
// Совместимость: данные для UI-модуля `AdminViolationsV2Module.tsx`
// (часть экрана использует упрощённые mock-данные; здесь поддерживаем экспорты)
// -----------------------------------------------------------------------------

export interface OffenderRatingRow {
  offender: string;
  region: string;
  article: string;
  violations: number;
}

export const offendersRatingArticles = [
  "ст.615 (Превышение скорости)",
  "ст.620 (Непристёгнутый ремень)",
  "ст.440 (Мелкое хулиганство)",
  "ст.467 (Распитие в общ. местах)",
  "ст.456 (Нарушение тишины)",
] as const;

export const offendersRatingRows: OffenderRatingRow[] = [
  {
    offender: "ИП 'Алтын-Трейд'",
    region: "г. Атырау",
    article: offendersRatingArticles[1],
    violations: 1240,
  },
  {
    offender: "ТОО 'CityTrans'",
    region: "Жылыойский район",
    article: offendersRatingArticles[0],
    violations: 1185,
  },
  {
    offender: "ИП 'Dala Food'",
    region: "Курмангазинский район",
    article: offendersRatingArticles[3],
    violations: 910,
  },
];

// Блок "Общая сводка"
export const mainKPIs: any = {
  totalViolations: { month: 1284500, ytd: 15391719, dynamicPercent: 2.3 },
  trafficFatalities: {
    count: 1847,
    trend: -5.2,
    accidents: 12450,
    injuries: 15680,
  },
  publicOrderViolations: { count: 324560, trend: 1.8 },
  businessViolations: { count: 186420, trend: -3.1 },
  specialCategories: {
    minors: 24566,
    domesticConflicts: 18450,
    alcoholDrugs: 89230,
  },
  repeatRate: { percent: 7.2, byRegionMax: 12.5, byRegionMin: 3.8 },
  processingDelays: { overdue: 8450, avgDays: 12.3, overduePercent: 2.1 },
};

export const heatmapRegionsData: any[] = [
  {
    name: "г. Атырау",
    violations: 4628310,
    per100k: 2150,
    level: "critical",
    lat: 43.222,
    lng: 76.8512,
  },
  {
    name: "Жылыойский район",
    violations: 2008244,
    per100k: 1820,
    level: "high",
    lat: 51.1694,
    lng: 71.4491,
  },
  {
    name: "Курмангазинский район",
    violations: 987810,
    per100k: 1650,
    level: "high",
    lat: 42.3419,
    lng: 69.5901,
  },
];

export const regionRankings: any = { improvements: [], deteriorations: [] };
export const paretoData: any[] = [];

export const trafficLightRisks: any[] = [
  { topic: "ДТП", level: "red", value: 1847, trend: 5.2 },
  { topic: "Общественный порядок", level: "yellow", value: 324560, trend: 1.8 },
  { topic: "Бизнес-среда", level: "green", value: 186420, trend: -3.1 },
];

export const trafficSafetyKPIs: any = {
  totalAccidents: 12450,
  fatalities: 1847,
  injuries: 15680,
  alcoholRelated: 2340,
  speedViolations: 4850000,
  repeatOffenders: 15200,
  dangerousZones: 245,
};

export const trafficViolationTypes = [
  { type: "Превышение скорости", count: 4850000, percent: 42.3 },
  { type: "Непристёгнутый ремень", count: 2150000, percent: 18.8 },
  { type: "Алкоголь/наркотики за рулём", count: 980000, percent: 8.6 },
  { type: "Проезд на красный", count: 680000, percent: 5.9 },
  { type: "Выезд на встречку", count: 420000, percent: 3.7 },
  { type: "Непропуск пешеходов", count: 380000, percent: 3.3 },
  { type: "Нарушения на перекрёстках", count: 340000, percent: 3.0 },
  { type: "Отсутствие техосмотра", count: 290000, percent: 2.5 },
  { type: "Без страховки", count: 260000, percent: 2.3 },
  { type: "Прочие", count: 1100000, percent: 9.6 },
];

export const fatalAccidentsMonthly = [
  { month: "Янв", fatalities: 142, accidents: 980, trend: -2.1 },
  { month: "Фев", fatalities: 128, accidents: 850, trend: -5.3 },
  { month: "Мар", fatalities: 156, accidents: 1020, trend: 3.2 },
  { month: "Апр", fatalities: 168, accidents: 1150, trend: 8.5 },
  { month: "Май", fatalities: 185, accidents: 1280, trend: 12.1 },
  { month: "Июн", fatalities: 198, accidents: 1350, trend: 15.4 },
  { month: "Июл", fatalities: 212, accidents: 1420, trend: 18.2 },
  { month: "Авг", fatalities: 195, accidents: 1380, trend: 14.8 },
  { month: "Сен", fatalities: 172, accidents: 1180, trend: 6.5 },
  { month: "Окт", fatalities: 148, accidents: 1050, trend: -1.2 },
  { month: "Ноя", fatalities: 135, accidents: 920, trend: -6.8 },
  { month: "Дек", fatalities: 108, accidents: 870, trend: -12.3 },
];

export const dangerousRoads = [
  { road: "г. Атырау - Қапшағай", km: "45-67", accidents: 89, fatalities: 12 },
  { road: "Жылыойский район - Қарағанды", km: "120-145", accidents: 72, fatalities: 9 },
  { road: "Курмангазинский район - Тараз", km: "80-102", accidents: 65, fatalities: 8 },
  { road: "г. Атырау - Бишкек", km: "15-35", accidents: 58, fatalities: 7 },
  { road: "Атырау - Ақтау", km: "210-245", accidents: 52, fatalities: 6 },
];

export const publicOrderKPIs: any = {
  hooliganism: 168420,
  minorHooliganism: 156140,
  drinkingPublic: 324560,
  noiseViolations: 89450,
  domesticConflicts: 18450,
  minorsViolations: 24566,
};

export const publicOrderByDistrict = [
  {
    district: "г. Атырау, Алмалинский р-н",
    violations: 12450,
    trend: 8.5,
    hotspots: 15,
  },
  {
    district: "г. Атырау, Медеуский р-н",
    violations: 11230,
    trend: 5.2,
    hotspots: 12,
  },
  {
    district: "Жылыойский район, Сарыарка р-н",
    violations: 9870,
    trend: 3.8,
    hotspots: 10,
  },
  {
    district: "Курмангазинский район, Әл-Фараби р-н",
    violations: 8560,
    trend: 6.1,
    hotspots: 8,
  },
  {
    district: "Жылыойский район, Байқоңыр р-н",
    violations: 7890,
    trend: 2.4,
    hotspots: 7,
  },
];

export const weeklySignals: any[] = [];
export const crimesByArticle: any[] = [];

export const businessEnvironmentKPIs: any = {
  totalViolations: 186420,
  tradeViolations: 45680,
  sanitaryViolations: 38450,
  laborViolations: 32560,
  constructionViolations: 28450,
  ecologyViolations: 24560,
  transportViolations: 16720,
  repeatOffendersPercent: 12.3,
  avgProcessingDays: 8.5,
};

export const businessCategories: any[] = [
  {
    category: "Торговля/права потребителей",
    violations: 45680,
    repeatOffenders: 5620,
    formalViolations: 28500,
    substantialViolations: 17180,
    avgProcessingTime: 7.2,
  },
  {
    category: "Санитария/общепит",
    violations: 38450,
    repeatOffenders: 4890,
    formalViolations: 22800,
    substantialViolations: 15650,
    avgProcessingTime: 9.5,
  },
  {
    category: "Труд и охрана труда",
    violations: 32560,
    repeatOffenders: 3980,
    formalViolations: 21400,
    substantialViolations: 11160,
    avgProcessingTime: 8.8,
  },
  {
    category: "Строительство/землепользование",
    violations: 28450,
    repeatOffenders: 3450,
    formalViolations: 18200,
    substantialViolations: 10250,
    avgProcessingTime: 12.3,
  },
  {
    category: "Экология/отходы/выбросы",
    violations: 24560,
    repeatOffenders: 2980,
    formalViolations: 14500,
    substantialViolations: 10060,
    avgProcessingTime: 15.2,
  },
  {
    category: "Транспорт/лицензирование",
    violations: 16720,
    repeatOffenders: 2120,
    formalViolations: 11200,
    substantialViolations: 5520,
    avgProcessingTime: 6.5,
  },
];

export const businessPressureByRegion = [
  { region: "г. Атырау", violationsPer1000: 12.5, trend: 2.3 },
  { region: "Жылыойский район", violationsPer1000: 10.8, trend: -1.5 },
  { region: "Курмангазинский район", violationsPer1000: 9.2, trend: 3.8 },
  { region: "Исатайский район", violationsPer1000: 8.5, trend: -2.1 },
  { region: "Макатский район", violationsPer1000: 7.8, trend: 1.2 },
];

export const topRepeatOffenders: any[] = [
  { category: "Общепит (сети)", violations: 1250, percent: 8.5 },
  { category: "Розничная торговля", violations: 980, percent: 6.7 },
  { category: "Строительные компании", violations: 750, percent: 5.1 },
  { category: "Транспортные компании", violations: 620, percent: 4.2 },
  { category: "Производство", violations: 540, percent: 3.7 },
];

export const efficiencyKPIs: any = {};
export const efficiencyFunnel: any[] = [];
export const overdueByRegion: any[] = [];
export const terminationReasons: any[] = [];
export const courtVsAuthorityReview: any[] = [];
export const processingTimeDistribution: any[] = [];
export const speedVsQualityByRegion: any[] = [];

export const specialCategoriesKPIs: any = {
  minors: {},
  domesticConflicts: {},
  repeatOffenders: {},
  alcoholDrugs: {},
  migration: {},
  weapons: {},
};
export const specialCategoriesDetails: any[] = [];

export const minorsMonthlyTrend = [
  { month: "Янв", count: 1850, adultInvolvement: 420 },
  { month: "Фев", count: 1720, adultInvolvement: 380 },
  { month: "Мар", count: 1980, adultInvolvement: 450 },
  { month: "Апр", count: 2120, adultInvolvement: 485 },
  { month: "Май", count: 2350, adultInvolvement: 540 },
  { month: "Июн", count: 2580, adultInvolvement: 590 },
  { month: "Июл", count: 2750, adultInvolvement: 630 },
  { month: "Авг", count: 2480, adultInvolvement: 570 },
  { month: "Сен", count: 2150, adultInvolvement: 490 },
  { month: "Окт", count: 1920, adultInvolvement: 440 },
  { month: "Ноя", count: 1780, adultInvolvement: 405 },
  { month: "Дек", count: 1886, adultInvolvement: 430 },
];

export const regionRadarData = [
  {
    region: "г. Атырау",
    minors: 85,
    domestic: 72,
    repeat: 68,
    alcohol: 90,
    migration: 95,
    weapons: 45,
  },
  {
    region: "Жылыойский район",
    minors: 62,
    domestic: 58,
    repeat: 55,
    alcohol: 75,
    migration: 88,
    weapons: 38,
  },
  {
    region: "Курмангазинский район",
    minors: 78,
    domestic: 82,
    repeat: 62,
    alcohol: 68,
    migration: 72,
    weapons: 42,
  },
  {
    region: "Исатайский район",
    minors: 48,
    domestic: 52,
    repeat: 45,
    alcohol: 58,
    migration: 45,
    weapons: 32,
  },
  {
    region: "Индерский район",
    minors: 72,
    domestic: 78,
    repeat: 58,
    alcohol: 55,
    migration: 68,
    weapons: 35,
  },
];

export const managementInsights: any = {
  trafficSafety: { summary: "", measures: [] },
  publicOrder: { summary: "", measures: [] },
  businessEnvironment: { summary: "", measures: [] },
  specialCategories: { summary: "", measures: [] },
};
